/**
 * editorDraft：Settings 表单的“可编辑草稿”模型。
 *
 * 设计意图：
 * - 将“当前选中的方案 + 编辑中的文本 + 分析范围”作为一个整体草稿，便于 reset/patch；
 * - `selectedApproachId` 统一为 string（或 `__custom__`），避免 Select 组件 value 类型在 number/string 间跳变导致受控异常。
 */
import type { IAiAnalysisScope, IAiPromptInfo, IAiPromptOption } from '../types';
import { normalizeAnalysisScope } from './scope';

export const CUSTOM_APPROACH_VALUE = '__custom__';

export interface IEditorDraft {
  selectedApproachId: number | string;
  promptContent: string;
  analysisContext: string;
  analysisScope: IAiAnalysisScope;
}

interface IBuildEditorDraftParams {
  promptOptions: IAiPromptOption[];
  allTaskIds: string[];
  defaultSelectedApproachId?: number | string;
  initialPromptInfo?: IAiPromptInfo;
}

export const getNormalizedApproachId = (
  approachId: number | string | undefined,
  promptOptions: IAiPromptOption[],
): string => {
  // 约束：Select 组件使用 string value；仅在内部做一次规范化，避免外部到处散落 String(...)。
  if (typeof approachId === 'number') {
    return String(approachId);
  }
  if (approachId === CUSTOM_APPROACH_VALUE) {
    return CUSTOM_APPROACH_VALUE;
  }
  return String(promptOptions?.[0]?.value ?? CUSTOM_APPROACH_VALUE);
};

export const buildEditorDraftFromParams = (params: IBuildEditorDraftParams): IEditorDraft => {
  const { promptOptions, allTaskIds, defaultSelectedApproachId, initialPromptInfo } = params;
  const selectedApproachId = getNormalizedApproachId(defaultSelectedApproachId, promptOptions);
  const analysisScope = normalizeAnalysisScope(initialPromptInfo?.analysisScope, allTaskIds);
  const firstOption = promptOptions?.[0];
  const firstPromptContent = String(firstOption?.info?.promptContent ?? '');
  const firstAnalysisContext = String(firstOption?.info?.analysisContext ?? '');
  if (selectedApproachId === CUSTOM_APPROACH_VALUE) {
    const localCustomPrompt = initialPromptInfo?.customPrompt;
    const hasLocalCustomPrompt = typeof localCustomPrompt?.promptContent === 'string'
      && typeof localCustomPrompt?.analysisContext === 'string';
    if (hasLocalCustomPrompt) {
      // 自定义方案优先回显用户历史输入；避免把模板 prompt 覆盖掉用户内容。
      return {
        selectedApproachId,
        promptContent: String(localCustomPrompt?.promptContent ?? ''),
        analysisContext: String(localCustomPrompt?.analysisContext ?? ''),
        analysisScope,
      };
    }
    return {
      selectedApproachId,
      promptContent: firstPromptContent,
      analysisContext: firstAnalysisContext,
      analysisScope,
    };
  }

  // 预置方案：回显该方案对应的模板内容；同时保持 scope 来自 promptInfo 或默认范围。
  const selectedOption = promptOptions.find((option) => String(option?.value) === selectedApproachId);
  return {
    selectedApproachId,
    promptContent: String(selectedOption?.info?.promptContent ?? ''),
    analysisContext: String(selectedOption?.info?.analysisContext ?? ''),
    analysisScope,
  };
};

export const patchEditorDraft = (
  currentDraft: IEditorDraft,
  patch: Partial<IEditorDraft>,
): IEditorDraft => ({
  ...currentDraft,
  ...patch,
});
